import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { companyAPI, testimonialsAPI, quoteAPI, contactAPI, authAPI, logoAPI } from '../utils/api';
import { useToast } from '../hooks/use-toast';
import { Settings, MessageSquare, FileText, Mail, KeyRound, LogOut, Wrench, Truck, Upload, Home, Search, MapPin } from 'lucide-react';
import ServicesManager from '../components/admin/ServicesManager';
import FleetManager from '../components/admin/FleetManager';
import HomeContentManager from '../components/admin/HomeContentManager';
import SeoManager from '../components/admin/SeoManager';
import CitiesManager from '../components/admin/CitiesManager';

const AdminContentManagement = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [companyRes, testimonialsRes, quotesRes, messagesRes] = await Promise.all([
        companyAPI.getInfo(),
        testimonialsAPI.getAll(),
        quoteAPI.getAll(),
        contactAPI.getAll(),
      ]);
      setCompanyInfo(companyRes.data);
      setTestimonials(testimonialsRes.data);
      setQuoteRequests(quotesRes.data);
      setContactMessages(messagesRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleUpdateCompanyInfo = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await companyAPI.updateInfo(companyInfo);
      toast({ title: 'Success', description: 'Company information updated' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast({ title: 'Error', description: 'New passwords do not match', variant: 'destructive' });
      return;
    }
    if (pwForm.new_password.length < 8) {
      toast({ title: 'Error', description: 'New password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    setPwLoading(true);
    try {
      await authAPI.changePassword({ current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast({ title: 'Success', description: 'Password updated successfully' });
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to change password', variant: 'destructive' });
    } finally {
      setPwLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const res = await logoAPI.upload(file);
      setCompanyInfo({ ...companyInfo, logo_url: res.data.logo_url });
      toast({ title: 'Success', description: 'Logo uploaded' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to upload logo', variant: 'destructive' });
    } finally {
      setLogoUploading(false);
    }
  };

  if (!companyInfo) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Content Management</h1>
            <p className="text-gray-600 mt-2">Manage website content and inquiries</p>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-button">
            <LogOut className="h-4 w-4 mr-2" />Logout
          </Button>
        </div>

        <Tabs defaultValue="company" className="space-y-6">
          <TabsList>
            <TabsTrigger value="company"><Settings className="h-4 w-4 mr-2" />Company Info</TabsTrigger>
            <TabsTrigger value="home" data-testid="home-tab"><Home className="h-4 w-4 mr-2" />Home Page</TabsTrigger>
            <TabsTrigger value="services" data-testid="services-tab"><Wrench className="h-4 w-4 mr-2" />Services</TabsTrigger>
            <TabsTrigger value="fleet" data-testid="fleet-tab"><Truck className="h-4 w-4 mr-2" />Fleet</TabsTrigger>
            <TabsTrigger value="cities" data-testid="cities-tab"><MapPin className="h-4 w-4 mr-2" />Cities</TabsTrigger>
            <TabsTrigger value="seo" data-testid="seo-tab"><Search className="h-4 w-4 mr-2" />SEO</TabsTrigger>
            <TabsTrigger value="testimonials"><MessageSquare className="h-4 w-4 mr-2" />Testimonials ({testimonials.length})</TabsTrigger>
            <TabsTrigger value="quotes"><FileText className="h-4 w-4 mr-2" />Quote Requests ({quoteRequests.length})</TabsTrigger>
            <TabsTrigger value="messages"><Mail className="h-4 w-4 mr-2" />Messages ({contactMessages.length})</TabsTrigger>
            <TabsTrigger value="security" data-testid="security-tab"><KeyRound className="h-4 w-4 mr-2" />Security</TabsTrigger>
          </TabsList>

          <TabsContent value="company">
            <Card>
              <CardHeader>
                <CardTitle>Edit Company Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateCompanyInfo} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Company Name</Label>
                      <Input value={companyInfo.company_name} onChange={(e) => setCompanyInfo({...companyInfo, company_name: e.target.value})} />
                    </div>
                    <div>
                      <Label>Established Year</Label>
                      <Input type="number" value={companyInfo.established_year} onChange={(e) => setCompanyInfo({...companyInfo, established_year: parseInt(e.target.value)})} />
                    </div>
                  </div>

                  <div>
                    <Label>Tagline</Label>
                    <Input value={companyInfo.tagline} onChange={(e) => setCompanyInfo({...companyInfo, tagline: e.target.value})} />
                  </div>

                  <div>
                    <Label>About</Label>
                    <Textarea rows={4} value={companyInfo.about} onChange={(e) => setCompanyInfo({...companyInfo, about: e.target.value})} />
                  </div>

                  <div>
                    <Label>Mission</Label>
                    <Textarea rows={3} value={companyInfo.mission} onChange={(e) => setCompanyInfo({...companyInfo, mission: e.target.value})} />
                  </div>

                  <div>
                    <Label>Vision</Label>
                    <Textarea rows={3} value={companyInfo.vision} onChange={(e) => setCompanyInfo({...companyInfo, vision: e.target.value})} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Phone</Label>
                      <Input value={companyInfo.contact_phone || ''} onChange={(e) => setCompanyInfo({...companyInfo, contact_phone: e.target.value})} data-testid="company-phone-input" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input type="email" value={companyInfo.contact_email || ''} onChange={(e) => setCompanyInfo({...companyInfo, contact_email: e.target.value})} data-testid="company-email-input" />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Input value={companyInfo.address || ''} onChange={(e) => setCompanyInfo({...companyInfo, address: e.target.value})} data-testid="company-address-input" />
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-semibold mb-3">Logo</h3>
                    <div className="flex items-center gap-4 mb-3">
                      {companyInfo.logo_url && <img src={companyInfo.logo_url} alt="Logo preview" className="h-16 w-16 object-contain border rounded bg-white p-1" data-testid="logo-preview" />}
                      <label className="cursor-pointer">
                        <span className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-md text-sm hover:bg-gray-800">
                          <Upload className="h-4 w-4 mr-2" />{logoUploading ? 'Uploading...' : 'Upload Image'}
                        </span>
                        <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={logoUploading} data-testid="logo-upload-input" />
                      </label>
                    </div>
                    <div>
                      <Label>...or paste Logo Image URL</Label>
                      <Input value={companyInfo.logo_url || ''} onChange={(e) => setCompanyInfo({...companyInfo, logo_url: e.target.value})} placeholder="https://... or leave blank for default" data-testid="logo-url-input" />
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-semibold mb-3">Page Hero Subtitles</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Services Subtitle</Label>
                        <Input value={companyInfo.services_subtitle || ''} onChange={(e) => setCompanyInfo({...companyInfo, services_subtitle: e.target.value})} placeholder="Comprehensive transportation solutions..." data-testid="services-subtitle-input" />
                      </div>
                      <div>
                        <Label>About Subtitle</Label>
                        <Input value={companyInfo.about_subtitle || ''} onChange={(e) => setCompanyInfo({...companyInfo, about_subtitle: e.target.value})} placeholder="Your trusted transportation partner..." data-testid="about-subtitle-input" />
                      </div>
                      <div>
                        <Label>Contact Subtitle</Label>
                        <Input value={companyInfo.contact_subtitle || ''} onChange={(e) => setCompanyInfo({...companyInfo, contact_subtitle: e.target.value})} placeholder="Get in touch with our team" data-testid="contact-subtitle-input" />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-semibold mb-3">About Page — "Why Businesses Trust Us" Cards</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Established Card Description</Label>
                        <Input value={companyInfo.trust_established_desc || ''} onChange={(e) => setCompanyInfo({...companyInfo, trust_established_desc: e.target.value})} placeholder="Over a decade of reliable service" data-testid="trust-established-input" />
                      </div>
                      <div />
                      <div>
                        <Label>Clients Card Title</Label>
                        <Input value={companyInfo.trust_clients_title || ''} onChange={(e) => setCompanyInfo({...companyInfo, trust_clients_title: e.target.value})} placeholder="50+ Corporate Clients" data-testid="trust-clients-title-input" />
                      </div>
                      <div>
                        <Label>Clients Card Description</Label>
                        <Input value={companyInfo.trust_clients_desc || ''} onChange={(e) => setCompanyInfo({...companyInfo, trust_clients_desc: e.target.value})} placeholder="Trusted by leading MNCs" data-testid="trust-clients-desc-input" />
                      </div>
                      <div>
                        <Label>Presence Card Title</Label>
                        <Input value={companyInfo.trust_presence_title || ''} onChange={(e) => setCompanyInfo({...companyInfo, trust_presence_title: e.target.value})} placeholder="Pan-India Presence" data-testid="trust-presence-title-input" />
                      </div>
                      <div>
                        <Label>Presence Card Description</Label>
                        <Input value={companyInfo.trust_presence_desc || ''} onChange={(e) => setCompanyInfo({...companyInfo, trust_presence_desc: e.target.value})} placeholder="Serving 100+ cities nationwide" data-testid="trust-presence-desc-input" />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-semibold mb-3">Social Media Links</h3>
                    <p className="text-sm text-gray-500 mb-3">Leave a field blank to hide that icon in the footer.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Facebook URL</Label>
                        <Input value={companyInfo.social_links?.facebook || ''} onChange={(e) => setCompanyInfo({...companyInfo, social_links: {...(companyInfo.social_links || {}), facebook: e.target.value}})} placeholder="https://facebook.com/..." data-testid="social-facebook-input" />
                      </div>
                      <div>
                        <Label>LinkedIn URL</Label>
                        <Input value={companyInfo.social_links?.linkedin || ''} onChange={(e) => setCompanyInfo({...companyInfo, social_links: {...(companyInfo.social_links || {}), linkedin: e.target.value}})} placeholder="https://linkedin.com/company/..." data-testid="social-linkedin-input" />
                      </div>
                      <div>
                        <Label>Twitter / X URL</Label>
                        <Input value={companyInfo.social_links?.twitter || ''} onChange={(e) => setCompanyInfo({...companyInfo, social_links: {...(companyInfo.social_links || {}), twitter: e.target.value}})} placeholder="https://twitter.com/..." data-testid="social-twitter-input" />
                      </div>
                      <div>
                        <Label>Instagram URL</Label>
                        <Input value={companyInfo.social_links?.instagram || ''} onChange={(e) => setCompanyInfo({...companyInfo, social_links: {...(companyInfo.social_links || {}), instagram: e.target.value}})} placeholder="https://instagram.com/..." data-testid="social-instagram-input" />
                      </div>
                      <div>
                        <Label>WhatsApp Link</Label>
                        <Input value={companyInfo.social_links?.whatsapp || ''} onChange={(e) => setCompanyInfo({...companyInfo, social_links: {...(companyInfo.social_links || {}), whatsapp: e.target.value}})} placeholder="https://wa.me/91XXXXXXXXXX" data-testid="social-whatsapp-input" />
                      </div>
                    </div>
                  </div>

                  <Button type="submit" disabled={loading} data-testid="company-save-button">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="services">
            <ServicesManager />
          </TabsContent>

          <TabsContent value="home">
            <HomeContentManager />
          </TabsContent>

          <TabsContent value="seo">
            <SeoManager />
          </TabsContent>

          <TabsContent value="fleet">
            <FleetManager />
          </TabsContent>

          <TabsContent value="cities">
            <CitiesManager />
          </TabsContent>

          <TabsContent value="testimonials">
            <Card>
              <CardHeader>
                <CardTitle>Testimonials</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {testimonials.map((t) => (
                    <div key={t.id} className="border rounded-lg p-4">
                      <h4 className="font-semibold">{t.client_name}</h4>
                      <p className="text-sm text-gray-600">{t.designation} at {t.company}</p>
                      <p className="mt-2 text-gray-700">"{t.message}"</p>
                      <p className="text-sm text-gray-500 mt-1">Rating: {t.rating}/5</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quotes">
            <Card>
              <CardHeader>
                <CardTitle>Quote Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {quoteRequests.map((q) => (
                    <div key={q.id} className="border rounded-lg p-4">
                      <h4 className="font-semibold">{q.name}</h4>
                      <p className="text-sm text-gray-600">{q.email} | {q.phone}</p>
                      {q.company && <p className="text-sm">Company: {q.company}</p>}
                      <p className="text-sm mt-2"><strong>Service:</strong> {q.service_type}</p>
                      <p className="text-sm text-gray-700 mt-2">{q.message}</p>
                      <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs ${q.status === 'new' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                        {q.status}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle>Contact Messages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {contactMessages.map((m) => (
                    <div key={m.id} className="border rounded-lg p-4">
                      <h4 className="font-semibold">{m.name}</h4>
                      <p className="text-sm text-gray-600">{m.email} | {m.phone}</p>
                      <p className="text-sm mt-2"><strong>Subject:</strong> {m.subject}</p>
                      <p className="text-sm text-gray-700 mt-2">{m.message}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                  <div>
                    <Label>Current Password</Label>
                    <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} required data-testid="current-password-input" />
                  </div>
                  <div>
                    <Label>New Password</Label>
                    <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} required data-testid="new-password-input" />
                    <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
                  </div>
                  <div>
                    <Label>Confirm New Password</Label>
                    <Input type="password" value={pwForm.confirm_password} onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })} required data-testid="confirm-password-input" />
                  </div>
                  <Button type="submit" disabled={pwLoading} data-testid="change-password-submit">
                    {pwLoading ? 'Updating...' : 'Update Password'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminContentManagement;
